import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import type {
  SnackbarFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { GetCurrentMemberFn } from '@/features/iam'
import { BookkeepingAddManualQuickBooksConnectionDialogViewModel } from '@/features/bookkeeping/components/BookkeepingAddManualConnectionDialog/BookkeepingAddManualQuickBooksConnectionDialogViewModel'
import type { QuickBooksAccount } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnection'
import type { BookkeepingPlatformConnectionStore } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnectionStore'
import type { BookkeepingConnectionTarget } from '@/features/bookkeeping/components/BookkeepingPlatformSelectionDialog/BookkeepingPlatformSelectionDialogViewModel'

/**
 * The view model options.
 */
export interface QuickBooksConnectionsDialogViewModelOptions {
  /**
   * The bookkeeping connection target.
   */
  target: BookkeepingConnectionTarget

  /**
   * The function to call when the user provides a connection.
   */
  onAction: (quickBooksAccount: QuickBooksAccount) => Promise<unknown>
}

export class QuickBooksConnectionsDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * Add connection manually view model.
   */
  readonly bookkeepingAddManualConnectionsDialogVm

  /**
   * The scroll provider.
   */
  scrollProvider = new InfiniteScrollProvider({
    limit: 10,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'Unable to load QuickBooks accounts',
        signal,
      )

      return withRetry(() =>
        this._store.listQuickBooksPlatformConnections(paging, signal),
      )
    },
  })

  constructor(
    private readonly _options: QuickBooksConnectionsDialogViewModelOptions,
    private readonly _store: BookkeepingPlatformConnectionStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this.bookkeepingAddManualConnectionsDialogVm =
      new BookkeepingAddManualQuickBooksConnectionDialogViewModel(
        this._store,
        this._newSnackbar,
        this._getCurrentMember,
        this._options.onAction,
      )
    this._lifecycle.manage(this.bookkeepingAddManualConnectionsDialogVm)
  }

  /**
   * The list of {@link QuickBooksAccount}.
   */
  @computed
  get accounts() {
    return this._store.quickBooksAccounts
  }

  /**
   * Whether the account list is empty.
   */
  @computed
  get isEmpty() {
    return this.scrollProvider.items.length === 0
  }

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()

    void this.scrollProvider.fetch({ signal: this._lifecycle.signal })
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Provides {@link QuickBooksAccount} platform connection.
   */
  providePlatformConnection = task.resolved(
    async (quickBooksAccount: QuickBooksAccount) => {
      try {
        await this._options.onAction(quickBooksAccount)
        this.close()
      } catch (err: unknown) {
        const snackbar = this._newSnackbar()
        snackbar.fromError(err)
      }
    },
  )

  /**
   * Redirects the user to the QuickBooks OAuth2 authorization page.
   */
  connectQuickBooks = task.resolved(() => {
    this._store.redirectToQuickBooksOauth2Page(this._options.target.returnUrl())
  })

  /**
   * Shows the add manually dialog.
   */
  @action.bound
  addConnectionManually() {
    this.bookkeepingAddManualConnectionsDialogVm.show()
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }
}
